import { isNil } from 'lodash'
import { Box, Grid, Typography } from '@mui/material'
import { formatDateTime } from 'utils/util'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { SectionHeader } from 'components/SectionHeader'
import { SweepstakesStatusLabel } from 'pages/Sweepstakes/SweepstakesStatusLabel'
import { type Sweepstake } from 'src/types/api'
import { useGetSweepstakesByGroupId } from 'hooks/api/useGetSweepstakesByGroupId'

export const LocationSweepstakeDrawingsSection = ({
  sweepstake,
}: {
  sweepstake: Sweepstake
}) => {
  const drawingsQuery = useGetSweepstakesByGroupId(String(sweepstake.groupId))
  const drawings = drawingsQuery.data

  if (drawingsQuery.isPending) {
    return <ActivityIndicator />
  }

  if (drawingsQuery.isError || isNil(drawingsQuery.data)) {
    return <Box>An error occurred fetching the drawings.</Box>
  }

  return (
    <>
      <SectionHeader title="Drawings" showButton={false} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          rowGap: '16px',
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          p: '16px',
          mb: '40px',
          mt: '15px',
        }}
      >
        <Grid container spacing={3}>
          {drawings?.map((drawing, index) => (
            <Grid item xs={12} key={drawing.id}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <Typography variant="h6">
                  Drawing {Number(index) + 1}
                </Typography>
                {drawing.status && (
                  <SweepstakesStatusLabel
                    status={drawing.sweepstakeData?.state ?? 'Draft'}
                  />
                )}
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>Start Date:</strong>{' '}
                    {formatDateTime(drawing.startDate, 'P p')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    <strong>End Date:</strong>{' '}
                    {formatDateTime(drawing.endDate, 'P p')}
                  </Typography>
                </Grid>

                {drawing.contestWinners?.length !== 0 &&
                  Array.from(
                    { length: drawing.numLocationWinners ?? 1 },
                    (_, i) => {
                      const winner = drawing.contestWinners?.filter(
                        (x) => x.contestId === drawing.id
                      )[i]
                      return (
                        <Grid item xs={12} key={i}>
                          <Typography variant="body1">
                            <strong>Winner #{i + 1}: </strong>{' '}
                            {`${String(winner?.prizeDescription)}`}
                            {Number(winner?.prizeAmount) !== 0
                              ? ` - $${Number(winner?.prizeAmount ?? 0).toFixed(
                                  2
                                )}`
                              : ''}
                          </Typography>
                        </Grid>
                      )
                    }
                  )}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  )
}
